/**
 * Sentry Configuration
 *
 * Centralized error logging and monitoring setup for production.
 * This file configures Sentry for React error tracking.
 */

import * as Sentry from "@sentry/react";
import config from "../config";

/**
 * Initialize Sentry error monitoring
 * Should be called once at application startup
 */
export const initSentry = () => {
  // Only initialize Sentry in production or if explicitly enabled
  if (!config.isProduction && !config.sentry?.enabled) {
    console.log('Sentry disabled for development environment');
    return;
  }

  // No DSN means Sentry is intentionally off for this build: stay quiet
  // (a warning here would print in every visitor's console).
  if (!config.sentry?.dsn) {
    return;
  }

  Sentry.init({
    dsn: config.sentry.dsn,
    environment: config.environment,
    release: `healing-hues@${process.env.REACT_APP_VERSION || '1.0.0'}`,

    // Performance monitoring
    tracesSampleRate: config.isProduction ? 0.1 : 1.0, // 10% in prod, 100% in dev

    // Session replay for debugging (only in production errors)
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: config.isProduction ? 1.0 : 0,

    // Filter sensitive data
    beforeSend(event, hint) {
      // Remove sensitive user data
      if (event.user) {
        delete event.user.email;
        delete event.user.ip_address;
      }

      // Remove sensitive form data
      if (event.request?.data) {
        const data = event.request.data;
        if (typeof data === 'object') {
          delete data.email;
          delete data.password;
          delete data.token;
        }
      }

      return event;
    },

    // Integration configuration - simplified for compatibility
    integrations: [
      // Note: BrowserTracing integration can be added separately if needed
      // Sentry.browserTracingIntegration(),
    ],
  });

  console.log('Sentry initialized for error monitoring');
};

/**
 * Manually capture an exception
 * @param {Error} error - The error to capture
 * @param {Object} context - Additional context
 */
export const captureException = (error, context = {}) => {
  if (config.sentry?.dsn) {
    Sentry.captureException(error, {
      tags: {
        component: context.component || 'unknown',
        action: context.action || 'unknown',
      },
      extra: context,
    });
  } else if (config.isDevelopment) {
    console.error('Sentry not configured, error logged to console:', error);
  }
};

/**
 * Capture a message for debugging
 * @param {string} message - The message to log
 * @param {string} level - Log level (info, warning, error)
 * @param {Object} context - Additional context
 */
export const captureMessage = (message, level = 'info', context = {}) => {
  if (config.sentry?.dsn) {
    Sentry.captureMessage(message, level);
  } else if (config.isDevelopment) {
    // Sentry levels include 'warning' and 'fatal', which have no console method
    const consoleMethod = { warning: 'warn', fatal: 'error' }[level] || level;
    (console[consoleMethod] || console.log)('Sentry message:', message, context);
  }
};

/**
 * Set user context for error tracking
 * @param {Object} user - User information (avoid sensitive data)
 */
export const setUserContext = (user) => {
  if (config.sentry?.dsn) {
    Sentry.setUser({
      id: user.id,
      // Avoid storing email or other PII
      sessionId: user.sessionId,
    });
  }
};

const sentryUtils = {
  initSentry,
  captureException,
  captureMessage,
  setUserContext,
};

export default sentryUtils;
